/**
 * Print template i18n — переиспользует JSON-локали из b2b-ui
 * (`apps/b2b-ui/src/assets/i18n/{lang}.json`). Single source of truth для
 * переводов в монорепо: b2b-ui читает их через @ngx-translate (HTTP loader),
 * b2b-demo (Vite) импортирует синканную копию из `src/i18n/{lang}.json`.
 *
 * Синканная копия живёт в `src/i18n/` и обновляется скриптом
 * `scripts/sync-i18n-translations.js` (запускается через prebuild hook —
 * см. `package.json`). Прямой cross-app import не работает: Dockerfile
 * b2b-demo копирует только `apps/b2b-demo/` в build context, b2b-ui
 * физически отсутствует. См. issue #1021 post-merge build failure.
 *
 * Print-специфичные ключи живут под namespace `print` в каждом JSON-файле.
 * Если ключ отсутствует в текущем языке — fallback на FR (default для
 * Autodis Luxembourg), затем сам ключ как последний ресорс (видно в QA).
 *
 * Языки: FR, EN, DE, RU, EL — из b2b-ui (admin UI поддерживает их в full
 * scope), LU — добавлен файл с одним только `print` namespace для печати.
 * PrintControls (sidebar) выставляет user-facing whitelist FR/EN/DE/LU —
 * RU/EL пока не показываем оператору, но если когда-нибудь добавим, поля
 * `print.*` уже готовы.
 */
import en from '../../i18n/en.json';
import fr from '../../i18n/fr.json';
import de from '../../i18n/de.json';
import lu from '../../i18n/lu.json';
import ru from '../../i18n/ru.json';
import el from '../../i18n/el.json';

const LOCALES = { en, fr, de, lu, ru, el };

/** Whitelist для PrintControls (Language select). Если расширяем — также
 *  убедиться что namespace `print` существует в соответствующем JSON. */
export const PRINT_LANGUAGES = ['fr', 'en', 'de', 'lu'];

/** Resolve a key из namespace `print` в указанном языке. Fallback chain:
 *  lang.print[key] → fr.print[key] → key (для видимости в QA). В dev
 *  доп. console.warn если ни в одном языке ключа не нашли — typo при
 *  добавлении новой строки тогда видно сразу. */
export function t(key, lang = 'fr') {
  const tryLang = LOCALES[lang]?.print?.[key];
  if (tryLang !== undefined) return tryLang;
  const tryFr = LOCALES.fr.print?.[key];
  if (tryFr !== undefined) return tryFr;
  if (import.meta.env.DEV) {
    console.warn(`[print/translations] Missing key "${key}" — not found in lang=${lang} or FR fallback.`);
  }
  return key;
}

/** Escape regex metachars в строке — пользовательский ввод/JSON-данные
 *  безопасно подставляются в `new RegExp(...)`. Без этого `print.units`
 *  с ключом типа `m²` или `l/100km` (или будущим `(km)`) ломал бы pattern.
 */
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Translate inventory-data string (`car.transmission`, `car.fuelType`,
 * `car.warrantyTerm` etc.) — данные лежат во французском (Autodis Luxembourg),
 * рендерятся в текущем языке оператора.
 *
 *  1. Direct lookup в `print.values` (полные строки типа "Essence Hybride")
 *  2. Если не нашли — заменяем известные слова + единицы (`print.units`).
 *  3. Иначе возвращаем оригинал (новая модель/спец без перевода видна
 *     оператору в FR — лучше пустоты).
 */
export function translateValue(rawValue, lang = 'fr') {
  if (!rawValue) return rawValue;
  if (lang === 'fr') return rawValue;

  const valueMap = LOCALES[lang]?.print?.values || {};
  const unitsMap = LOCALES[lang]?.print?.units || {};

  // 1. Full string lookup
  if (valueMap[rawValue]) return valueMap[rawValue];

  // 2. Token replacement. Composite values (длиннее) — first, чтобы
  //    "Essence Hybride" не съелось ранним "Essence".
  let result = rawValue;
  const composites = Object.keys(valueMap).sort((a, b) => b.length - a.length);
  for (const src of composites) {
    if (result.includes(src)) {
      result = result.split(src).join(valueMap[src]);
    }
  }

  // Единицы — "224ch" (без пробела) и "7 ans" (с пробелом).
  for (const [src, dst] of Object.entries(unitsMap)) {
    const safe = escapeRegex(src);
    const tightRe = new RegExp(`(\\d)${safe}\\b`, 'g');
    result = result.replace(tightRe, `$1${dst}`);
    const wordRe = new RegExp(`\\b${safe}\\b`, 'g');
    result = result.replace(wordRe, dst);
  }

  return result;
}
